import { useMemo, useState } from "react";
import {
  AssessmentGroupListFragmentFragment,
  AssessmentType,
  ComplianceProgramStep,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, isAdminVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import ToggleSwitch from "@components/toggle-switch/toggle-switch";
import InfoTooltip from "@components/tooltip/info-tooltip";
import WarningTooltip from "@components/tooltip/warning-tooltip";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";

import AssessmentGroupSidebar from "../assessment-group-sidebar/assessment-group-sidebar";
import { ProgramForm, ProgramFormStep } from "../types";
import ComplianceProgramFormStep from "./compliance-program-form-step";
import CoreValueAssessmentSettingsModal from "./core-value-assessment-settings-modal";
import GoalAssessmentSettingsModal from "./goal-assessment-settings-modal";
import QuestionSetSelector from "./question-set-selector";
import RoleBasedAssessmentSettingsModal from "./role-based-assessment-settings-modal";

const PeerAssessmentStep = ({
  proposedComplianceProgram,
  canUpdate,
  onAddOrUpdateStep,
  availableAssessmentGroups,
  onAssessmentGroupCreated,
  onAssessmentGroupArchived,
}: {
  proposedComplianceProgram: ProgramForm;
  canUpdate: boolean;
  onAddOrUpdateStep: (step: ProgramFormStep) => void;
  availableAssessmentGroups: AssessmentGroupListFragmentFragment[];
  onAssessmentGroupCreated: (
    newAssessmentGroup: AssessmentGroupListFragmentFragment,
  ) => void;
  onAssessmentGroupArchived: (assessmentGroupId: number) => void;
}) => {
  const label = useLabel();
  const organization = currentOrganizationVar();
  const isAdmin = isAdminVar();
  const hasGoalBasedAssessments = organization.featureFlags.goals;

  const [isRoleBasedAssessmentModalOpen, setIsRoleBasedAssessmentModalOpen] =
    useState(false);
  const [isGoalBasedAssessmentModalOpen, setIsGoalBasedAssessmentModalOpen] =
    useState(false);
  const [
    isCoreValueBasedAssessmentModalOpen,
    setIsCoreValueBasedAssessmentModalOpen,
  ] = useState(false);
  const [isAssessmentGroupSidebarOpen, setIsAssessmentGroupSidebarOpen] =
    useState(false);
  const [assessmentGroupToDuplicate, setAssessmentGroupToDuplicate] =
    useState<AssessmentGroupListFragmentFragment | null>(null);

  const availableAssessmentGroupSelectOptions = useMemo(() => {
    return availableAssessmentGroups
      .filter(({ assessmentType }) => assessmentType === AssessmentType.Peer)
      .filter(
        (assessmentGroup) =>
          !assessmentGroup.isOnlySelfAssessment &&
          !assessmentGroup.hasNpsQuestions,
      )
      .map(({ id, title }) => ({
        value: id,
        label: title,
      }));
  }, [availableAssessmentGroups]);

  const peerReviewStep = useMemo(() => {
    return proposedComplianceProgram.steps.find(
      (step) => step.stepType === ComplianceProgramStep.PeerReview,
    );
  }, [proposedComplianceProgram]);

  const selectedAssessmentGroup = useMemo(() => {
    return peerReviewStep && peerReviewStep.questionSetIds.length > 0
      ? (availableAssessmentGroups.find(
          ({ id }) => id === peerReviewStep.questionSetIds[0],
        ) ?? null)
      : null;
  }, [availableAssessmentGroups, peerReviewStep]);

  return (
    peerReviewStep && (
      <>
        {isAssessmentGroupSidebarOpen && (
          <AssessmentGroupSidebar
            assessmentType={AssessmentType.Peer}
            assessmentGroupId={selectedAssessmentGroup?.id}
            assessmentGroupToDuplicateId={assessmentGroupToDuplicate?.id}
            onClose={() => {
              setAssessmentGroupToDuplicate(null);
              setIsAssessmentGroupSidebarOpen(false);
            }}
            onAssessmentGroupCreated={(assessmentGroup) => {
              onAssessmentGroupCreated(assessmentGroup);
              onAddOrUpdateStep({
                ...peerReviewStep,
                questionSetIds: [assessmentGroup.id],
              });
            }}
            onAssessmentGroupArchived={(assessmentGroupId) =>
              onAssessmentGroupArchived(assessmentGroupId)
            }
          />
        )}
        {isRoleBasedAssessmentModalOpen && (
          <RoleBasedAssessmentSettingsModal
            assessmentType={AssessmentType.Peer}
            onClose={() => setIsRoleBasedAssessmentModalOpen(false)}
            canUpdate={canUpdate}
            selectedPerformanceAssessmentGroup={peerReviewStep}
            onChangeSettings={(settings) => {
              onAddOrUpdateStep({
                ...peerReviewStep,
                roleBasedReviewConfig: {
                  ...peerReviewStep.roleBasedReviewConfig,
                  ...settings,
                },
              });
            }}
          />
        )}
        {isGoalBasedAssessmentModalOpen && (
          <GoalAssessmentSettingsModal
            assessmentType={AssessmentType.Peer}
            onClose={() => setIsGoalBasedAssessmentModalOpen(false)}
            canUpdate={canUpdate}
            selectedPerformanceAssessmentGroup={peerReviewStep}
            onChangeSettings={(settings) => {
              onAddOrUpdateStep({
                ...peerReviewStep,
                goalReviewConfig: {
                  ...peerReviewStep.goalReviewConfig,
                  ...settings,
                },
              });
            }}
          />
        )}
        {isCoreValueBasedAssessmentModalOpen && (
          <CoreValueAssessmentSettingsModal
            assessmentType={AssessmentType.Peer}
            onClose={() => setIsCoreValueBasedAssessmentModalOpen(false)}
            canUpdate={canUpdate}
            selectedPerformanceAssessmentGroup={peerReviewStep}
            onChangeSettings={(settings) => {
              onAddOrUpdateStep({
                ...peerReviewStep,
                coreValueReviewConfig: {
                  ...peerReviewStep.coreValueReviewConfig,
                  ...settings,
                },
              });
            }}
          />
        )}
        <ComplianceProgramFormStep
          title={`Peer ${label("review", { capitalize: true })}`}
        >
          <div className="px-6 pb-6 flex flex-col gap-3">
            <div className="flex items-center gap-3 p-4 bg-white border rounded">
              <QuestionSetSelector
                assessmentType={AssessmentType.Peer}
                availableAssessmentGroupSelectOptions={
                  availableAssessmentGroupSelectOptions
                }
                canUpdate={canUpdate}
                onUpdateAssessmentTemplate={(assessmentGroupId) => {
                  onAddOrUpdateStep({
                    ...peerReviewStep,
                    questionSetIds: assessmentGroupId
                      ? [assessmentGroupId]
                      : [],
                  });
                }}
                onDuplicateAssessmentGroup={setAssessmentGroupToDuplicate}
                onOpenAssessmentGroupSidebar={() =>
                  setIsAssessmentGroupSidebarOpen(true)
                }
                selectedAssessmentGroup={selectedAssessmentGroup}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-white border rounded">
              <div className="flex items-center gap-2">
                <div className="text-sm">{`Role ${label("review", {
                  capitalize: true,
                })}`}</div>
                <InfoTooltip text="Use this if you would like to assess an individual based on their job responsibilities and competencies." />
              </div>

              {!organization.hasCareerFramework ? (
                <WarningTooltip
                  size={6}
                  text={`There are no Career Tracks defined for your ${label(
                    "organization",
                  )}.${
                    isAdmin
                      ? ` Go to ${label("organization", {
                          capitalize: true,
                        })} settings to define them.`
                      : ""
                  }`}
                />
              ) : (
                <div className="flex items-center gap-4">
                  <Button
                    className="mb-1"
                    disabled={
                      !peerReviewStep ||
                      !peerReviewStep.roleBasedReviewConfig
                        .enabledForCurrentRole
                    }
                    icon={TopicflowIconMap.edit}
                    theme={buttonTheme.iconGray}
                    onClick={() => setIsRoleBasedAssessmentModalOpen(true)}
                  />
                  <ToggleSwitch
                    disabled={!canUpdate || !peerReviewStep}
                    checked={
                      !!peerReviewStep?.roleBasedReviewConfig
                        .enabledForCurrentRole
                    }
                    onChange={(enabled) =>
                      onAddOrUpdateStep({
                        ...peerReviewStep,
                        roleBasedReviewConfig: {
                          ...peerReviewStep.roleBasedReviewConfig,
                          enabledForCurrentRole: enabled,
                          enabledForNextRole: false,
                          enabledSelfReviewForNextRole: false,
                        },
                      })
                    }
                  />
                </div>
              )}
            </div>

            {hasGoalBasedAssessments && (
              <div className="flex items-center justify-between p-4 bg-white border rounded">
                <div className="flex items-center gap-2">
                  <div className="text-sm">{`${label("goal", {
                    capitalize: true,
                  })} ${label("review", {
                    capitalize: true,
                  })}`}</div>
                  <InfoTooltip
                    text={`Use this if you would like to assess an individual's progress towards their ${label(
                      "goal",
                      {
                        pluralize: true,
                      },
                    )}.`}
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Button
                    className="mb-1"
                    disabled={
                      !peerReviewStep ||
                      !peerReviewStep.goalReviewConfig.enabled
                    }
                    icon={TopicflowIconMap.edit}
                    theme={buttonTheme.iconGray}
                    onClick={() => setIsGoalBasedAssessmentModalOpen(true)}
                  />
                  <ToggleSwitch
                    disabled={!canUpdate || !peerReviewStep}
                    checked={!!peerReviewStep?.goalReviewConfig.enabled}
                    onChange={(enabled) =>
                      onAddOrUpdateStep({
                        ...peerReviewStep,
                        goalReviewConfig: {
                          ...peerReviewStep.goalReviewConfig,
                          enabled,
                        },
                      })
                    }
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between p-4 bg-white border rounded">
              <div className="flex items-center gap-2">
                <div className="text-sm">{`Values ${label("review", {
                  capitalize: true,
                })}`}</div>
                <InfoTooltip text="Use this if you would like to assess an individual's alignment with your Core Values" />
              </div>

              {organization.coreValues?.edges.length === 0 ? (
                <WarningTooltip
                  size={6}
                  text={`There are no Core Values defined for your ${label(
                    "organization",
                  )}.${
                    isAdmin
                      ? ` Go to ${label("organization", {
                          capitalize: true,
                        })} settings to define them.`
                      : ""
                  }`}
                />
              ) : (
                <div className="flex items-center gap-4">
                  <Button
                    className="mb-1"
                    disabled={
                      !peerReviewStep ||
                      !peerReviewStep.coreValueReviewConfig.enabled
                    }
                    icon={TopicflowIconMap.edit}
                    theme={buttonTheme.iconGray}
                    onClick={() => setIsCoreValueBasedAssessmentModalOpen(true)}
                  />
                  <ToggleSwitch
                    disabled={!canUpdate || !peerReviewStep}
                    checked={!!peerReviewStep?.coreValueReviewConfig.enabled}
                    onChange={(enabled) =>
                      onAddOrUpdateStep({
                        ...peerReviewStep,
                        coreValueReviewConfig: {
                          ...peerReviewStep.coreValueReviewConfig,
                          enabled,
                        },
                      })
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </ComplianceProgramFormStep>
      </>
    )
  );
};
export default PeerAssessmentStep;
